import {
  EuiButton,
  EuiButtonEmpty,
  EuiFieldText,
  EuiForm,
  EuiFormRow,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiTextArea,
} from "@inscopix/ideas-eui";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { getEnvVar } from "ideas.env";
import { useState } from "react";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { getRequestHeaders } from "utils/getRequestHeaders";
import { isDjangoError } from "utils/isDjangoError";

/**
 * Creates a new tool.
 * @param options The options for the new tool.
 */
const createTool = async (options: {
  name: string;
  key: string;
  description: string;
  tenantKey: string;
}) => {
  const baseUrl = getEnvVar("URL_TRS");
  const url = `${baseUrl}tool/`;
  const headers = await getRequestHeaders();
  const body = {
    name: options.name,
    key: `${options.tenantKey}.${options.key}`,
    help_text: options.description,
  };
  await axios.post(url, body, { headers });
};

/** Regular expression for invalid tool key characters */
const INVALID_KEY_CHARACTERS = /[^a-z0-9-_]/g;

/**
 * Formats the tool name into a URL-friendly key.
 * @param name The name of the tool.
 * @returns The formatted key.
 */
const formatKey = (name: string) => {
  return (
    name
      .toLowerCase()
      // Replace spaces with underscores
      .replaceAll(" ", "_")
      // Remove invalid characters
      .replaceAll(INVALID_KEY_CHARACTERS, "")
  );
};

/**
 * Type guard to check if an error is a key uniqueness error.
 * @param error The error to check.
 * @returns `true` if the error is a key uniqueness error, `false` otherwise.
 */
const isKeyUniqueError = (error: Error) => {
  if (axios.isAxiosError(error)) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const data = error.response?.data;
    return (
      isDjangoError(data) &&
      data.errors.length > 0 &&
      data.errors[0].code === "unique" &&
      data.errors[0].attr === "key"
    );
  } else {
    return false;
  }
};

interface ModalCreateToolProps {
  tenantKey: string;
  onClose: () => void;
}

export const ModalCreateTool = ({
  tenantKey,
  onClose,
}: ModalCreateToolProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [isKeyDirty, setIsKeyDirty] = useState(false);
  const [isKeyTaken, setIsKeyTaken] = useState(false);
  const [description, setDescription] = useState("");
  const queryClient = useQueryClient();

  const handleClick = async () => {
    try {
      setIsLoading(true);
      await createTool({ name, key, description, tenantKey });
      await queryClient.invalidateQueries(["all_tools"]);
      onClose();
    } catch (error) {
      addUtilityToastFailure("Failed to create tool");
      if (isKeyUniqueError(error as Error)) {
        setIsKeyTaken(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <EuiModal onClose={onClose}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>Create Tool</EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody>
        <EuiForm>
          <EuiFormRow label="Name">
            <EuiFieldText
              autoFocus
              placeholder="My Tool"
              value={name}
              onChange={(e) => {
                const newName = e.target.value;
                setName(newName);
                if (!isKeyDirty) {
                  setKey(formatKey(newName));
                }
              }}
              name="name"
            />
          </EuiFormRow>

          <EuiFormRow
            label="Key"
            helpText="Enter an identifier to use as this tool's key - allows letters, numbers, dashes, and underscores. Must be unique within your organization. Your organization's key will be prefixed automatically."
            isInvalid={isKeyTaken}
            error="Key is already in use by another tool in your organization"
          >
            <EuiFieldText
              placeholder="my_tool"
              value={key}
              onChange={(e) => {
                setKey(formatKey(e.target.value));
                setIsKeyDirty(true);
                setIsKeyTaken(false);
              }}
              name="key"
            />
          </EuiFormRow>

          <EuiFormRow label="Description">
            <EuiTextArea
              placeholder="Describe your tool here"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              name="description"
              rows={4}
            />
          </EuiFormRow>
        </EuiForm>
      </EuiModalBody>

      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose}>Cancel</EuiButtonEmpty>

        <EuiButton
          fill
          onClick={() => void handleClick()}
          disabled={name === "" || key === "" || isKeyTaken}
          isLoading={isLoading}
        >
          Create
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};
